import React from 'react';
import { Database, Network, PencilLine, Sparkles } from 'lucide-react';
import {
  StartPickerShell,
  StartPickerGenerateButton,
  StartPickerCard,
  StartPickerBackLink,
} from '../../../components/start-picker/StartPicker';
import { useOrgModel } from '../hooks/useOrgModel';

/**
 * Org chart Start picker — built from the shared start-picker primitives
 * (components/start-picker), uniform with the flowchart picker. Shown once
 * over a freshly-created, empty org chart with no pre-chosen seed.
 *
 * When the folder has canonical people records, starting FROM THAT DATA is
 * the primary path (source-first: the folder's dataset is the core; blank
 * drawing is the exception). AI prompt and manual starter stay available.
 */

export interface OrgChartStartPickerProps {
  /** Fires with a Seed Registry id ('org-25-sample', 'org-from-source'). */
  onSelectStarter: (seedId: string) => void;
  onGenerateFromPrompt: (prompt: string) => Promise<boolean>;
  isGeneratingPrompt?: boolean;
  /** Folder the new diagram lives in — enables the source-backed starter. */
  folderId?: string | null;
}

const SAMPLE_PROMPT = `Create a simple org chart for a small product company.

Maya Chen is CEO.
Alex leads Product, with Priya and Noah reporting to Alex.
Jordan leads Sales, with Mia and Leo reporting to Jordan.
Sam leads Operations, with Ava reporting to Sam.`;

export const OrgChartStartPicker: React.FC<OrgChartStartPickerProps> = ({
  onSelectStarter,
  onGenerateFromPrompt,
  isGeneratingPrompt = false,
  folderId,
}) => {
  const orgModel = useOrgModel({ folderId: folderId ?? null, enabled: Boolean(folderId) });
  const sourcePeopleCount = orgModel.data?.people?.length ?? 0;
  const [promptOpen, setPromptOpen] = React.useState(false);
  const [prompt, setPrompt] = React.useState(SAMPLE_PROMPT);
  const [error, setError] = React.useState<string | null>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  React.useEffect(() => {
    if (!promptOpen) return;
    try { window.dispatchEvent(new Event('assistant:collapse')); } catch { /* noop */ }
    const frame = window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.select();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [promptOpen]);

  const handlePromptSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    const generated = await onGenerateFromPrompt(prompt);
    if (!generated) {
      setError('Could not generate this org chart. Try a little more detail or start manually.');
    }
  };

  if (promptOpen) {
    return (
      <StartPickerShell
        icon={<Sparkles className="size-5" />}
        title="Describe your org chart"
        subtitle="Use names, roles, reporting lines, or paste a rough team list. This example is selected so you can replace it immediately."
        testId="tid.onboarding.orgStartPicker.prompt"
        maxWidthClass="max-w-xl"
        topSlot={
          <StartPickerBackLink
            label="Back"
            onClick={() => {
              setError(null);
              setPromptOpen(false);
            }}
            testId="tid.onboarding.orgStartPicker.prompt.back"
          />
        }
      >
        <form className="rounded-[14px] border border-[color:var(--color-ink-100)] bg-white p-4 shadow-[0_8px_24px_rgba(13,42,77,0.08)]" onSubmit={handlePromptSubmit}>
          <label className="mb-2 block text-[12px] font-semibold text-[color:var(--color-navy-900)]" htmlFor="org-start-prompt">
            Org chart prompt
          </label>
          <textarea
            ref={textareaRef}
            id="org-start-prompt"
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            disabled={isGeneratingPrompt}
            data-testid="tid.onboarding.orgStartPicker.prompt.input"
            className="min-h-[180px] w-full resize-none rounded-[10px] border border-[color:var(--color-ink-200)] bg-white px-3 py-2.5 text-[13px] leading-[1.5] text-[color:var(--color-navy-900)] outline-none transition focus:border-[color:var(--color-coral-300)] focus:ring-2 focus:ring-[color:var(--color-coral-100)] disabled:opacity-70"
          />
          {error ? (
            <div className="mt-2 text-[12px] text-[color:var(--color-coral-700)]" data-testid="tid.onboarding.orgStartPicker.prompt.error">
              {error}
            </div>
          ) : null}
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setError(null);
                setPromptOpen(false);
              }}
              disabled={isGeneratingPrompt}
              className="rounded-[9px] border border-[color:var(--color-ink-200)] bg-white px-3 py-2 text-[12px] font-semibold text-[color:var(--color-ink-600)] transition hover:bg-[color:var(--color-ink-50)] disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isGeneratingPrompt || prompt.trim().length === 0}
              data-testid="tid.onboarding.orgStartPicker.prompt.submit"
              className="rounded-[9px] bg-[color:var(--color-coral-500)] px-3.5 py-2 text-[12px] font-semibold text-white transition hover:bg-[color:var(--color-coral-600)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isGeneratingPrompt ? 'Generating...' : 'Generate chart'}
            </button>
          </div>
        </form>
      </StartPickerShell>
    );
  }

  const hasSourceData = sourcePeopleCount > 0;

  return (
    <StartPickerShell
      icon={<Network className="size-5" />}
      title="Start your org chart"
      subtitle={hasSourceData
        ? 'Start from the people data already in this folder, or begin another way.'
        : 'Use AI to create a first draft, or start with a simple editable team.'}
      testId="tid.onboarding.orgStartPicker"
      maxWidthClass="max-w-xl"
    >
      {hasSourceData ? (
        <div className="grid grid-cols-1">
          <StartPickerCard
            icon={<Database className="size-6" />}
            name={`Start from your data (${sourcePeopleCount} people)`}
            description="Build an editable chart from this folder's people records — names, roles, and reporting lines filled in."
            onClick={() => onSelectStarter('org-from-source')}
            testId="tid.onboarding.orgStartPicker.fromSource"
          />
        </div>
      ) : null}
      <StartPickerGenerateButton
        title="Start with a prompt"
        description="Describe the team in your own words."
        onClick={() => setPromptOpen(true)}
        testId="tid.onboarding.orgStartPicker.generate"
      />
      <div className="grid grid-cols-1">
        <StartPickerCard
          icon={<PencilLine className="size-6" />}
          name="Draw manually"
          description="Use a simple 3-layer starter with Tree off and photos off."
          onClick={() => onSelectStarter('org-manual-starter')}
          testId="tid.onboarding.orgStartPicker.manual"
        />
      </div>
    </StartPickerShell>
  );
};
